// src/pages/public/AudioGift/AudioGiftEdit.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from '../../../public/icons';
import { MinusIcon, PlusIcon, GiftIcon } from '../../../public/icons';
import CustomizeModal from '../../../component/audiogifts/CustomizeModal';
import ReviewModal from '../../../component/general/ReviewModal';
import CustomerReviews from '../../../component/general/CustomerReviews';
import {
  AudioGift,
  AudioGift1,
  AudioGift2,
  AudioGift3
} from '../../../public/images/shop';

/* ─────────────────────────────────────────────
   Draft persistence helpers
   ───────────────────────────────────────────── */
const DRAFT_PREFIX = 'kapta_draft_';
const MODAL_DRAFT_KEY = 'kapta_audiogift_modal_draft';
const STEP_DRAFT_KEY = 'kapta_audiogift_step_customize';

const loadDraft = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load draft:', err);
    return null;
  }
};

const saveDraft = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save draft:', err);
  }
};

const clearDraftByKey = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.error('Failed to clear draft:', err);
  }
};

const AudioGiftEdit = () => {
  const location = useLocation();

  /* ── Stable draft key from URL (survives reload) ── */
  const draftKey =
    `${DRAFT_PREFIX}${location.pathname
      .replace(/^\//, '')
      .replace(/\//g, '-')
      .replace(/-+/g, '-')}` ||
    `${DRAFT_PREFIX}audio-gift_default`;

  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [reviewsRefreshKey, setReviewsRefreshKey] = useState(0);
  const [isAdding, setIsAdding] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const draftHydratedRef = useRef(false);

  /* ───────── HYDRATE on mount ───────── */
  useEffect(() => {
    const draft = loadDraft(draftKey);
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (typeof draft.quantity === 'number') setQuantity(draft.quantity);
    if (typeof draft.currentImageIndex === 'number')
      setCurrentImageIndex(draft.currentImageIndex);

    draftHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  /* ───────── AUTOSAVE on every change ───────── */
  useEffect(() => {
    if (!draftHydratedRef.current) return;

    saveDraft(draftKey, {
      quantity,
      currentImageIndex
    });
  }, [draftKey, quantity, currentImageIndex]);

  /* Save on refresh / tab close */
  useEffect(() => {
    const saveNow = () => {
      if (!draftHydratedRef.current) return;
      saveDraft(draftKey, {
        quantity,
        currentImageIndex
      });
    };
    window.addEventListener('beforeunload', saveNow);
    return () => window.removeEventListener('beforeunload', saveNow);
  }, [draftKey, quantity, currentImageIndex]);

  const productImages = [AudioGift, AudioGift1, AudioGift2, AudioGift3];

  const title = 'Audio Gift';
  const price = 300000;
  const inStock = true;
  const PRODUCT_SLUG = 'audio-gift';

  /* ───────── Toast helper ───────── */
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  /* ───────── Add to bag ───────── */
  const handleAddToBag = async (audioData) => {
    if (isAdding) return;

    if (!inStock) {
      showToast('This item is out of stock', 'error');
      return;
    }

    try {
      setIsAdding(true);

      await new Promise((resolve) => setTimeout(resolve, 400));

      const cartItem = {
        cartItemId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id: PRODUCT_SLUG,
        slug: PRODUCT_SLUG,
        title: audioData.title || `${title} - Custom`,
        description:
          audioData.description ||
          'A voice message scheduled for a special moment.',
        isCustom: true,
        quantity: audioData.quantity || quantity,
        price: audioData.price || price,
        lineTotal:
          (audioData.price || price) * (audioData.quantity || quantity),
        image: audioData.image || productImages[0],

        giftItem: audioData.customization?.giftItem || null,
        giftItemLabel: audioData.customization?.giftItemLabel || '',
        messageTitle: audioData.customization?.messageTitle || '',
        revealDate: audioData.customization?.revealDate || null,
        recipient: audioData.customization?.recipient || null,

        customization: audioData.customization || {},
        audioFile: audioData.audioFile || null,
        audioUrl: audioData.audioUrl || '',
        audioSource: audioData.customization?.audioSource || null
      };

      const existing = JSON.parse(
        localStorage.getItem('memoryMagnetsCart') || '[]'
      );
      existing.push(cartItem);
      localStorage.setItem('memoryMagnetsCart', JSON.stringify(existing));

      /* 🟢 Clear ALL drafts — committed to cart */
      clearDraftByKey(draftKey);
      localStorage.removeItem(MODAL_DRAFT_KEY);
      localStorage.removeItem(STEP_DRAFT_KEY);

      /* 🟢 Notify the app + open the shopping bag */
      window.dispatchEvent(new Event('cartUpdated'));
      window.dispatchEvent(new CustomEvent('openShoppingBag'));

      showToast('Added to bag', 'success');
      setShowCustomizeModal(false);
      setQuantity(1);
    } catch (err) {
      console.error('Failed to add to bag:', err);
      showToast('Failed to add to bag. Please try again.', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  /* ───────── Customize save ───────── */
  const handlePersonalizeSave = (personalizedData) => {
    const finalData = {
      title: `${title} - Custom`,
      description: 'A voice message scheduled for a special moment.',
      quantity,
      price,
      image: productImages[0],
      customization: personalizedData || {},
      audioFile: personalizedData?.audioFile || null,
      audioUrl: personalizedData?.audioUrl || ''
    };

    handleAddToBag(finalData);
  };

  /* ───────── Progress bridge for the Customize modal ───────── */
  const handleCustomizeProgress = (progress) => {
    if (!progress) return;
    try {
      localStorage.setItem(STEP_DRAFT_KEY, JSON.stringify(progress));
    } catch (err) {
      console.error('Failed to save audiogift step draft:', err);
    }
  };

  /* ───────── Image nav ───────── */
  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };

  /* ───────── Button ───────── */
  const renderAddToBagButton = () => {
    return (
      <button
        onClick={() => setShowCustomizeModal(true)}
        disabled={!inStock || isAdding}
        className={`py-3 sm:py-4 text-base sm:text-lg font-medium w-full sm:w-auto sm:min-w-[280px] md:w-[393px] transition-colors flex items-center justify-center space-x-2 ${
          !inStock || isAdding
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-[#D2A146] text-[#FCF7EE] hover:bg-[#C4956A]'
        }`}
      >
        <GiftIcon />
        <span>Customise</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#FCF7EE]">
      {/* ───────── Toast Popup ───────── */}
      {toast.show && (
        <div
          className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999]"
          style={{ animation: 'fadeInDown 0.3s ease-out' }}
        >
          <div
            className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-xl border ${
              toast.type === 'success'
                ? 'bg-[#E7F4ED] border-[#2F8F5B] text-[#2F8F5B]'
                : toast.type === 'error'
                ? 'bg-[#F4E7E7] border-[#A82121] text-[#A82121]'
                : 'bg-[#E7ECF4] border-[#2F578F] text-[#2F578F]'
            }`}
          >
            {toast.type === 'success' && (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="#2F8F5B" strokeWidth="2" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="#2F8F5B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span className="font-medium text-sm sm:text-base">
              {toast.message}
            </span>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 max-w-[85rem] pt-20 sm:pt-24 md:pt-28 lg:pt-32">
        <div className="flex flex-col lg:flex-row justify-between gap-6 md:gap-8 lg:gap-10">
          {/* Left: Images */}
          <div className="w-full lg:w-1/2">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex flex-row sm:flex-col gap-2 sm:gap-2.5 order-2 sm:order-1">
                {productImages.map((image, index) => (
                  <div
                    key={index}
                    className={`w-14 h-14 sm:w-16 sm:h-16 cursor-pointer border-2 flex-shrink-0 ${
                      currentImageIndex === index
                        ? 'border-[#D2A146]'
                        : 'border-transparent'
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>

              <div className="relative overflow-hidden order-1 sm:order-2 flex-1">
                <img
                  src={productImages[currentImageIndex]}
                  alt={title}
                  className="w-full max-w-[498px] h-[300px] sm:h-[400px] md:h-[500px] lg:h-[632px] object-cover mx-auto"
                />

                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-[#FCF7EE] hover:bg-[#E8E0D5] flex items-center justify-center transition-colors rounded-full border border-[#DBDBDB]"
                >
                  <ArrowLeft size={16} color="#1C2A46" />
                </button>

                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-[#FCF7EE] hover:bg-[#E8E0D5] flex items-center justify-center transition-colors rounded-full border border-[#DBDBDB]"
                >
                  <ArrowRight size={16} color="#1C2A46" />
                </button>
              </div>
            </div>
          </div>

          {/* Right: Info */}
          <div className="w-full lg:w-1/2">
            <div className="space-y-6 sm:space-y-8 text-left">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1C2A46] mb-2">
                  Audio Gift
                </h1>
                <p className="font-light text-xs sm:text-sm text-[#55000C] mb-2">
                  A story you can hold, open, and revisit.
                </p>
                <p className="text-sm sm:text-base md:text-lg text-[#6B635D] leading-relaxed">
                  Audio Gift lets you record a heartfelt voice message and
                  schedule it to be delivered on a future date — birthdays,
                  anniversaries, encouragement days, or "just because."
                </p>
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-medium text-[#1C2A46] mb-2">
                  What Included
                </h3>
                <div className="bg-[#F5F5F5] px-3 sm:px-4 py-4 sm:py-5">
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        1 audio message
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        Scheduled delivery
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        Digital replay link
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center justify-between space-x-4 w-32 sm:w-36 p-1.5 border border-[#E0E0E0]">
                    <button
                      onClick={() =>
                        setQuantity((prev) => Math.max(1, prev - 1))
                      }
                      className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                      disabled={isAdding}
                    >
                      <MinusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                    </button>
                    <span className="text-sm sm:text-base font-normal text-[#1C2A46] min-w-[2.5rem] sm:min-w-[3rem] text-center">
                      {quantity} set
                    </span>
                    <button
                      onClick={() => setQuantity((prev) => prev + 1)}
                      className="rounded-lg flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                      disabled={isAdding}
                    >
                      <PlusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                    </button>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-[#6B635D] italic mb-6 sm:mb-7">
                  Each set contains 1 audio message
                </p>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
                  <div className="mb-4 sm:mb-6">
                    <span className="text-2xl sm:text-3xl font-medium text-[#1C2A46]">
                      ₦{(price * quantity).toLocaleString()}
                    </span>
                    <div className="text-xs sm:text-sm text-[#6B635D] mt-1">
                      {quantity} set of audio gift
                    </div>
                  </div>

                  <div className="flex-1">{renderAddToBagButton()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="text-left mt-12 sm:mt-16 md:mt-24 lg:mt-32 mb-6 sm:mb-8">
          <h3 className="text-2xl sm:text-3xl font-medium text-[#1C2A46] mb-6 sm:mb-8 border-b-2 pb-4 sm:pb-6 border-bordercolor">
            Product Details
          </h3>
          <p className="text-sm sm:text-base text-[#6B635D] mb-6 sm:mb-8 leading-relaxed">
            Audio Gift is a voice-first keepsake designed for people who want to
            say something that matters — and have it land at exactly the right
            moment. Record a message in your own voice, choose a beautiful
            keepsake to carry it (a Teddy Bear or a Wooden Alarm Clock), and
            schedule the reveal for a date that means something: a birthday, an
            anniversary, a milestone, or a day someone simply needs to hear you.
            <br />
            <br />
            <strong>Why it's special:</strong>
            <br />
            Your voice carries emotion text never can.
            <br />
            Perfect for long-distance love.
            <br />
            Surprise someone on a hard day.
            <br />
            Preserve a memory in sound.
          </p>

          <h4 className="text-lg sm:text-xl font-medium text-[#1C2A46] mb-3 sm:mb-4">
            Specifications
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 text-sm">
            <div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Audio Length
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Up to 5 minutes
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Delivery
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Scheduled date
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b border-bordercolor">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Format
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  MP3 / Digital link
                </span>
              </div>
            </div>
            <div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Replay
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Lifetime access
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Packaging
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Digital + optional keepsake
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b border-bordercolor">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Made In
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Nigeria
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <CustomerReviews
          slug={PRODUCT_SLUG}
          onWriteReview={() => setShowReviewModal(true)}
          refreshKey={reviewsRefreshKey}
        />
      </div>

      {/* Customize Modal */}
      <CustomizeModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onContinue={handlePersonalizeSave}
        onProgressChange={handleCustomizeProgress}
        isAdding={isAdding}
      />

      {/* Reviews modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setReviewsRefreshKey((k) => k + 1);
        }}
        product={{
          id: PRODUCT_SLUG,
          title: title,
          image: productImages[0],
          slug: PRODUCT_SLUG,
          price: price,
          category: 'Audio Gift',
          description: 'A voice message scheduled for a special moment',
          inStock: inStock
        }}
      />

      <style jsx global>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default AudioGiftEdit;